import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api, errorText } from '../lib/api'
import { keys, useTags } from '../lib/queries'
import type { Tag } from '../lib/types'
import { Button, Dialog, Input, Section, useToast } from '../components/ui'

/**
 * Tags, and the one place they can be removed.
 *
 * Tags are shared by every project, so deleting one takes it off other
 * people's tasks — which is why it lives here rather than in the picker where
 * a mis-tap is a real risk. The count is shown before you confirm, because
 * "delete Bug" reads very differently when it is on 40 cards.
 */
export function TagsSection() {
  const tags = useTags()
  const queryClient = useQueryClient()
  const toast = useToast()
  const [doomed, setDoomed] = useState<Tag | null>(null)
  const [editing, setEditing] = useState<string | null>(null)
  const [draft, setDraft] = useState('')

  function refresh() {
    queryClient.invalidateQueries({ queryKey: keys.tags() })
    // A renamed or removed tag changes what every board shows.
    queryClient.invalidateQueries({ queryKey: ['board'] })
    queryClient.invalidateQueries({ queryKey: ['tasks'] })
  }

  const rename = useMutation({
    mutationFn: (v: { tag: Tag; name: string; color: string }) =>
      api.updateTag(v.tag.id, v.name, v.color),
    onSuccess: () => {
      refresh()
      setEditing(null)
    },
    onError: (e) => toast.show('Could not save the tag', { tone: 'error', detail: errorText(e) }),
  })

  const remove = useMutation({
    mutationFn: (tag: Tag) => api.deleteTag(tag.id),
    onSuccess: (_, tag) => {
      refresh()
      setDoomed(null)
      toast.show(`Deleted ${tag.name}`)
    },
    onError: (e) => toast.show('Could not delete the tag', { tone: 'error', detail: errorText(e) }),
  })

  const list = tags.data ?? []

  return (
    <Section
      title="Tags"
      hint="Tags are shared by every project. Deleting one removes it from the tasks that use it; the tasks stay."
    >
      {list.length === 0 ? (
        <p className="text-[12.5px] text-[var(--ink-3)]">
          No tags yet. They are created from the tag picker on a task.
        </p>
      ) : (
        <div className="space-y-1.5">
          {list.map((tag) => (
            <div
              key={tag.id}
              className="flex items-center gap-2.5 rounded-md border border-[var(--line)] px-2.5 py-1.5 text-[13px]"
            >
              <input
                type="color"
                value={tag.color}
                onChange={(e) => rename.mutate({ tag, name: tag.name, color: e.target.value })}
                aria-label={`Colour for ${tag.name}`}
                className="h-5 w-5 shrink-0 cursor-pointer rounded border border-[var(--line)] bg-transparent p-0"
              />

              {editing === tag.id ? (
                <Input
                  autoFocus
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onBlur={() => {
                    if (draft.trim() && draft.trim() !== tag.name) {
                      rename.mutate({ tag, name: draft.trim(), color: tag.color })
                    } else {
                      setEditing(null)
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') e.currentTarget.blur()
                    if (e.key === 'Escape') setEditing(null)
                  }}
                  className="h-7 max-w-[220px] py-0 text-[13px]"
                  aria-label={`Name for ${tag.name}`}
                />
              ) : (
                <button
                  onClick={() => {
                    setEditing(tag.id)
                    setDraft(tag.name)
                  }}
                  className="truncate text-left hover:text-[var(--accent)]"
                >
                  {tag.name}
                </button>
              )}

              <span className="task-key ml-auto shrink-0 text-[11px] text-[var(--ink-3)]">
                {tag.usageCount ?? 0} {(tag.usageCount ?? 0) === 1 ? 'task' : 'tasks'}
              </span>
              <button
                onClick={() => setDoomed(tag)}
                className="shrink-0 text-[11px] text-[var(--ink-3)] hover:text-[var(--danger)]"
              >
                Delete
              </button>
            </div>
          ))}
        </div>
      )}

      <Dialog
        open={doomed !== null}
        onOpenChange={(open) => !open && setDoomed(null)}
        title={`Delete ${doomed?.name ?? ''}?`}
        description={
          (doomed?.usageCount ?? 0) > 0
            ? `This takes the tag off ${doomed?.usageCount} task${doomed?.usageCount === 1 ? '' : 's'} across every project. The tasks themselves are not touched.`
            : 'Nothing is using this tag.'
        }
        width={420}
      >
        <div className="flex justify-end gap-2">
          <Button onClick={() => setDoomed(null)}>Keep it</Button>
          <Button
            variant="danger"
            loading={remove.isPending}
            onClick={() => doomed && remove.mutate(doomed)}
          >
            Delete tag
          </Button>
        </div>
      </Dialog>
    </Section>
  )
}

